import { formatValue } from "./utils"

interface SafeDescriptionInput {
  description: string | null | undefined
  value?: number | null
  valueLabel?: string | null
  valueUnit?: string | null
  valuePrefix?: boolean
}

const answerContextWords = [
  "area",
  "box office",
  "cost",
  "download",
  "earn",
  "earning",
  "follower",
  "imdb",
  "market",
  "msrp",
  "net worth",
  "population",
  "price",
  "rank",
  "rating",
  "salary",
  "sales",
  "score",
  "search",
  "speed",
  "stream",
  "subscriber",
  "value",
  "view",
  "worth",
]

function normalizeText(value: string) {
  return value
    .toLowerCase()
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim()
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

function trimTrailingZeroes(value: string) {
  return value.replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "")
}

function addUnitVariants(
  variants: Set<string>,
  value: string,
  unit: string | null | undefined,
  prefix: boolean | undefined
) {
  if (!unit) return

  if (prefix) {
    variants.add(`${unit}${value}`)
    variants.add(`${unit} ${value}`)
  } else {
    variants.add(`${value}${unit}`)
    variants.add(`${value} ${unit}`)
  }
}

function buildValueVariants(value: number, unit: string | null | undefined, prefix: boolean | undefined) {
  const variants = new Set<string>()
  if (!Number.isFinite(value)) return variants

  const exact = value.toString()
  const localized = value.toLocaleString("en-US")
  variants.add(exact)
  variants.add(localized)

  if (Number.isInteger(value)) {
    variants.add(Math.trunc(value).toString())
    variants.add(Math.trunc(value).toLocaleString("en-US"))
  } else {
    for (const decimals of [1, 2]) {
      const fixed = value.toFixed(decimals)
      variants.add(fixed)
      variants.add(trimTrailingZeroes(fixed))
    }
  }

  variants.add(formatValue(value))
  variants.add(formatValue(value, unit || undefined, prefix))

  const abs = Math.abs(value)
  const suffixes: Array<[number, string]> = [
    [1_000_000_000, "B"],
    [1_000_000, "M"],
    [1_000, "K"],
  ]
  for (const [divisor, suffix] of suffixes) {
    if (abs >= divisor) {
      const scaled = value / divisor
      for (const decimals of [1, 2]) {
        const formatted = trimTrailingZeroes(scaled.toFixed(decimals))
        variants.add(`${formatted}${suffix}`)
        addUnitVariants(variants, `${formatted}${suffix}`, unit, prefix)
      }
    }
  }

  for (const variant of [...variants]) {
    addUnitVariants(variants, variant, unit, prefix)
    if (unit?.startsWith("/")) {
      variants.add(`${variant}${unit}`)
    }
  }

  return variants
}

function hasAnswerContext(description: string, valueLabel: string | null | undefined) {
  const labelWords = normalizeText(valueLabel || "")
    .split(/[^a-z0-9]+/)
    .filter((word) => word.length > 2)

  const words = [...answerContextWords, ...labelWords]
  return words.some((word) => description.includes(word))
}

function containsVariant(description: string, variant: string) {
  const normalizedVariant = normalizeText(variant)
  if (!normalizedVariant) return false

  const pattern = escapeRegExp(normalizedVariant).replace(/\s+/g, "\\s*")
  const matcher = new RegExp(`(^|[^a-z0-9])${pattern}($|[^a-z0-9])`, "i")
  return matcher.test(description)
}

function isDistinctiveVariant(variant: string, value: number, unit: string | null | undefined) {
  return (
    Math.abs(value) >= 1000 ||
    Boolean(unit && variant.includes(unit)) ||
    /[,./$€£¥%]/.test(variant) ||
    /\d(?:\.\d+)?[kmb]$/i.test(variant.trim())
  )
}

export function descriptionContainsAnswer({
  description,
  value,
  valueLabel,
  valueUnit,
  valuePrefix,
}: SafeDescriptionInput) {
  if (!description || value == null || !Number.isFinite(value)) return false

  const normalizedDescription = normalizeText(description)
  const hasContext = hasAnswerContext(normalizedDescription, valueLabel)
  const variants = buildValueVariants(value, valueUnit, valuePrefix)

  for (const variant of variants) {
    if (!containsVariant(normalizedDescription, variant)) continue
    if (isDistinctiveVariant(variant, value, valueUnit) || hasContext) {
      return true
    }
  }

  return false
}

export function getSafeGameItemDescription(input: SafeDescriptionInput) {
  if (!input.description) return null
  return descriptionContainsAnswer(input) ? null : input.description
}
